import { createContext, useContext, ReactNode } from 'react';

export type SetSkipRemount = (skip: boolean) => void;
export interface RouteTransitionGateValue {
  skipRemount: boolean;
  setSkipRemount: SetSkipRemount;
}

const RouteTransitionGateContext = createContext<RouteTransitionGateValue>({
  skipRemount: false,
  setSkipRemount: () => {},
});

interface RouteTransitionGateProviderProps {
  children: ReactNode;
  value: RouteTransitionGateValue;
}

export const RouteTransitionGateProvider = ({
  children,
  value,
}: RouteTransitionGateProviderProps) => {
  return (
    <RouteTransitionGateContext.Provider value={value}>
      {children}
    </RouteTransitionGateContext.Provider>
  );
};

/**
 * { skipRemount, setSkipRemount } -- lets a routed page hold back
 * AppGateway's route-set swap (which remounts the whole RouterProvider)
 * while a flow that flips `adviceOnboarded` is still navigating.
 *
 * Motivating case: accepting holistic advice / completing advice onboarding
 * flips `adviceOnboarded`, which makes AppGateway swap from
 * AdviceOnboardingRoutes to AdviceRoutes. If that swap lands while the flow
 * (e.g. SaveChangesOverview -> ... -> DownloadPlanView, or WhereNextView on
 * its own) is still mid-navigation, the whole tree remounts and drops
 * whatever navigate() call was in flight.
 *
 * Call setSkipRemount(true) before an action that might flip the flag, and
 * setSkipRemount(false) once the flow reaches a safe point to swap -- either
 * right after navigating (self-contained flows), or, if a later screen owns
 * that decision, whenever that screen finishes.
 */
export function useSkipRemount(): RouteTransitionGateValue {
  return useContext(RouteTransitionGateContext);
}

/**
 * Navigate to `path`, bypassing the currently-mounted router if it might
 * still be the advice-onboarding one about to be swapped out.
 *
 * `shouldBypassRouter` should be `!adviceOnboarded || skipRemount`: true
 * whenever the onboarding router could still be the one mounted (either
 * nothing has flipped `adviceOnboarded` yet, or it flipped but an earlier
 * screen is deliberately still holding the gate open).
 *
 * Bypassing means updating the URL directly via pushState instead of this
 * router's own navigate(): pushState doesn't fire popstate, so the
 * currently-mounted router won't re-render against it, and whatever router
 * AppGateway builds next reads window.location and initializes at `path`
 * fresh -- avoiding a frame of this route tree's own (possibly different)
 * content at `path`.
 */
export function navigateAfterOnboarding(
  path: string,
  shouldBypassRouter: boolean,
  navigate: (path: string, options?: { preserveLayout?: boolean }) => void
) {
  if (shouldBypassRouter) {
    window.history.pushState(null, '', path);
  } else {
    navigate(path, { preserveLayout: false });
  }
}
